import { GroupProps } from "@react-three/fiber";
import { useMemo } from "react";
import * as THREE from "three";

type GroundFogProps = GroupProps & {
  /**
   * Overall tint of the fog cards.
   */
  color?: THREE.ColorRepresentation;
  /**
   * Overall opacity multiplier (each card has its own base opacity).
   */
  opacity?: number;
};

function makeRadialGradientTexture(size = 128) {
  // Procedural radial gradient (matches the old 2d-canvas version:
  // alpha 1 → 0.8 at 45% → 0 at the edge).
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - c, y - c) / c;
      let a;
      if (d >= 1) a = 0;
      else if (d <= 0.45) a = 1 - (d / 0.45) * 0.2;
      else a = 0.8 * (1 - (d - 0.45) / 0.55);
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(a * 255);
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  // Why DataTexture and not CanvasTexture: WebKit's texture upload from a 2d
  // canvas corrupts this texture — the fog cards render with colored confetti
  // dots (iOS/macOS Safari only; disabling mipmaps alone did not help).
  // Uploading raw bytes avoids the canvas path entirely. Mipmaps stay off:
  // a soft radial gradient loses nothing under plain linear minification.
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export default function GroundFog({
  color = "#d5dde5",
  opacity = 1,
  ...props
}: GroundFogProps) {
  const alphaTex = useMemo(() => makeRadialGradientTexture(256), []);

  const materialProps = useMemo(() => {
    return {
      color,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      // Fog cards should always blend softly over the scene.
      opacity: 0.8 * opacity,
      map: alphaTex ?? undefined,
      alphaMap: alphaTex ?? undefined,
      // Avoid hard edges when cards overlap.
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
    } as const;
  }, [alphaTex, color, opacity]);

  return (
    <group {...props}>
      <mesh renderOrder={10}>
        <planeGeometry args={[55, 55]} />
        <meshBasicMaterial {...materialProps} />
      </mesh>
    </group>
  );
}
